import { HttpHeaders } from '@angular/common/http';
import { ErrorHandler, inject, Injectable, Injector, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { StatusCodes } from 'http-status-codes';
import { Observable, Subject, Subscription } from 'rxjs';
import { bufferTime, map, take } from 'rxjs/operators';
import {
  AnalyticsFacade,
  isFunction,
  isObject,
  NotificationService,
  objToStr,
  WfErrorMessage,
  WINDOW,
} from 'wflow-cdk';
import { AuthService } from 'wflow-main-app/src/app/auth/auth.service';
import { APP_ROUTES } from 'wflow-main-app/src/app/routing.const';

export interface ErrorMessage {
  message: string;
  error: any;
  status: number;
}

export type ErrorData<T = WfErrorMessage> = {
  headers?: HttpHeaders;
  status?: number;
  statusText?: string;
  url?: string;
  ok?: boolean;
  name?: string;
  title: string;
  message: string;
  error: T;
};

export type BackendError = {
  error: ErrorData;
  status?: number;
  url?: string;
};

export const UNKNOWN_ERROR = 0;

@Injectable()
export class BasicErrorHandlerService
  extends ErrorHandler
  implements OnDestroy
{
  private readonly _router = inject(Router);
  private readonly _authSvc = inject(AuthService);
  private readonly _translate = inject(TranslateService);
  private readonly _injector = inject(Injector);
  private readonly _window = inject(WINDOW);

  private readonly _subs = new Subscription();
  private readonly _chunkErrorPattern =
    /Loading chunk|Failed to fetch dynamically imported module|error loading dynamically imported module|Failed to load module script|\.loadModuleFactory/i;

  private readonly _exemptErrorMessages = [
    // For PDF.js, because there is no way to suppress the error inside the plugin. The error is expected.
    'Worker was terminated.',
    'Transport destroyed',
    // tesseract.js error ignore
    '/tesseract.js-core@',
    // For Angular ERROR: Expression has changed after it was checked.
    'ExpressionChangedAfterItHasBeenCheckedError',
    // Google tag manager error
    'www.googletagmanager.com',
    // Smartlook errors
    'InternalError: too much recursion',

    // for "Empty error"
    'no elements in sequence',
  ];

  private readonly _analyticExceptErrorMessages = ['","descriptions":["'];

  private readonly _messageBufferTime = 300;
  private readonly _messagesToSend$ = new Subject<ErrorMessage>();

  constructor() {
    super();
    this._createSendUniqueMessagesSubscription();
  }

  public handleError(error: ErrorData) {
    if (this._isChunkLoadError(error)) {
      this._window.location.reload();
      return;
    }

    if (this.ignoreError(error)) {
      return;
    }

    if (WfErrorMessage.isErrorMessage(error?.error)) {
      const err = error.error;
      if (error.title) {
        err.title = error.title;
      }
      this._messagesToSend$.next({
        message: err.description,
        error: err,
        status: 0,
      });
      return;
    }

    super.handleError(error);

    if (error.status === StatusCodes.UNAUTHORIZED) {
      this._authSvc
        .renewToken()
        .pipe(take(1))
        .subscribe((user) => {
          if (user == null) {
            this._redirectToLogin();
          }
        });
    } else {
      switch (error.status) {
        // case UNKNOWN_ERROR:
        case StatusCodes.GATEWAY_TIMEOUT:
          this._router.navigateByUrl(`/${APP_ROUTES.MAINTENANCE}`);
          break;
        case StatusCodes.FORBIDDEN:
          this._messagesToSend$.next({
            message: this._translate.instant('RequestedOperationIsForbidden'),
            error: {
              title: `${this._translate.instant('Forbidden403')}${
                error?.title ? ` - ${error.title}` : ''
              }`,
            },
            status: error.status,
          });
          break;
        case StatusCodes.NOT_FOUND:
          break;
        case StatusCodes.BAD_REQUEST:
          this._messagesToSend$.next({
            message: error.message,
            error: error,
            status: error.status,
          });
          break;
        default:
          if (error?.status == null) {
            if (!this._isAnalyticErrorExempt(error)) {
              this._injector.get(AnalyticsFacade).trackException(
                {
                  error: error as Error,
                },
                {
                  catchSource: 'BasicErrorHandlerService',
                  showToast: true,
                },
              );
            }
          }

          this._messagesToSend$.next({
            message: this._translate.instant(
              'BasicErrorHandlerService_DefaultErrorTitle',
            ),
            error: error,
            status: error?.status ?? 0,
          });
      }
    }
  }

  public ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  private _showError(
    message: string,
    error,
    status?: number,
    infinite = false,
  ) {
    let msg = message;
    if (error.useReload) {
      msg =
        msg +
        '<br/>' +
        this._translate.instant(
          'BasicErrorHandlerService_RefreshPageOnToastClickMessage',
        );
    }

    const errorJson = objToStr(error);

    this._sendToast(
      msg,
      error.title
        ? error.title
        : this._translate.instant('BasicErrorHandlerService_DefaultErrorTitle'),
      infinite,
      true,
      true,
      `error-code-${status}`,
      errorJson,
    ).subscribe((toast) => {
      if (error.callback) {
        error.callback();
      } else if (error.useReload) {
        this._window.location.reload();
      }
    });
  }

  private _isErrorExempt(error: unknown): boolean {
    const errorStr = String(error);

    return (
      !!error &&
      this._exemptErrorMessages.some((exemptErrorMessage) => {
        // Escape special regex characters
        const escapedMessage = exemptErrorMessage.replace(
          /[.*+?^${}()|[\]\\]/g,
          '\\$&',
        );
        return new RegExp(escapedMessage).test(errorStr);
      })
    );
  }

  private _isAnalyticErrorExempt(error: unknown): boolean {
    const errorStr = String(error);

    return (
      !!error &&
      this._analyticExceptErrorMessages.some((exemptErrorMessage) => {
        // Escape special regex characters
        const escapedMessage = exemptErrorMessage.replace(
          /[.*+?^${}()|[\]\\]/g,
          '\\$&',
        );
        return new RegExp(escapedMessage).test(errorStr);
      })
    );
  }

  private _redirectToLogin() {
    const redirectUrl = this._window.location.pathname;

    if (redirectUrl.startsWith(`/${APP_ROUTES.REGISTRATION}`)) {
      return;
    }

    this._router.navigate([
      `/${APP_ROUTES.UNAUTHORIZED}`,
      {
        redirectUrl,
      },
    ]);
  }

  private _sendToast(
    text: any,
    title?: string,
    infinite?: boolean,
    closable?: boolean,
    sendFromOutsideAngularZone?: boolean,
    errorStatus?: string,
    errorJson?: string,
  ): Observable<any> {
    return this._injector
      .get(NotificationService)
      .error({
        text: text,
        title: title,
        infinite,
        closable,
        sendFromOutsideAngularZone,
        errorStatus,
        errorJson,
      })
      .onHidden.pipe(take(1));
  }

  private _createSendUniqueMessagesSubscription() {
    this._subs.add(
      this._messagesToSend$
        .pipe(
          bufferTime(this._messageBufferTime),
          map((errorMessages) => {
            return this._removeMessageDuplicates(errorMessages);
          }),
        )
        .subscribe((errorMessages) => {
          errorMessages.forEach((errorMessage) => {
            this._showError(
              errorMessage.message,
              errorMessage.error,
              errorMessage.status,
            );
          });
        }),
    );
  }

  private readonly _removeMessageDuplicates = (array: ErrorMessage[]) => {
    const prop = 'message';
    return array.filter((obj, pos, arr) => {
      return arr.map((mapObj) => mapObj[prop]).indexOf(obj[prop]) === pos;
    });
  };

  private _isChunkLoadError(error: ErrorData): boolean {
    const message = error?.message ?? '';
    const name = error?.name ?? '';
    return name === 'ChunkLoadError' || this._chunkErrorPattern.test(message);
  }

  private ignoreError(error: unknown): boolean {
    return (
      (!!error && isObject(error) && this._isErrorExempt(error)) ||
      isFunction(error)
    );
  }
}
